/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react'
import api from '../../services/axios'
import { RankSubChart } from './RankSubChart'
import styled, { keyframes } from 'styled-components'
import {
  FaCircleChevronDown,
  FaCircleChevronUp,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaAnglesLeft,
  FaAnglesRight,
} from 'react-icons/fa6'
import { IoCloseCircleSharp } from 'react-icons/io5'
import { FaCalendarAlt, FaStar, FaExclamationTriangle, FaBoxes, FaFileExcel, FaFilePdf, FaSearch, FaTimes, FaStore } from 'react-icons/fa'
import { ThreeDot } from 'react-loading-indicators'
import {
  XLSX,
  buildCorporateSheet,
  cellText,
  cellMoney,
  cellNumber,
  cellTotalLabel,
  cellTotalMoney,
  cellTotalNumber,
} from '../../utils/excelCorporateStyle'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { normalizeDateForComparison, parseSafeDate, formatDateBr, formatCaixa, formatCurrencyBr } from '../../constantes'

import { useGeralClienteFilter } from '../../context/FiltersContext/ClientsPeriodoFiltro'
import { toast } from 'react-toastify'

const shimmer = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`

const PageContainer = styled.div`
  padding: 20px;
  min-height: 100vh;
  font-family: sans-serif;
  color: ${(props) => props.theme.textPrimary};
  max-width: 100vw;
  box-sizing: border-box;
  overflow-x: hidden;

  scrollbar-color: #a6c10b transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar,
  *::-webkit-scrollbar {
    display: block;
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track,
  *::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &::-webkit-scrollbar-thumb,
  *::-webkit-scrollbar-thumb {
    background: #a6c10b !important;
    border-radius: 10px !important;
  }
  &::-webkit-scrollbar-thumb:hover,
  *::-webkit-scrollbar-thumb:hover {
    background: #c2dc1b !important;
  }

  @media (max-width: 768px) {
    padding: 10px;
  }
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    color: ${(props) => props.theme.titleColor} !important;
  }
  p {
    color: ${(props) => props.theme.subtitleColor} !important;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`

const SwitchWrapper = styled.div`
  display: flex;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : '#082663')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'transparent')};
  border-radius: 8px;
  padding: 4px;
  width: auto;

  @media (max-width: 768px) {
    width: 100%;
    box-sizing: border-box;
  }
`

const SwitchButton = styled.button<{ $active: boolean }>`
  background: ${(props) => (props.$active ? '#a6c10b' : 'transparent')};
  color: ${(props) => (props.$active ? '#000' : props.theme.name === 'light' ? '#475569' : '#fff')};
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    flex: 1;
  }
`

const DashboardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const TableCard = styled.div`
  background: ${(props) => props.theme.cardBackground};
  border: 1px solid ${(props) => props.theme.cardBorder};
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 4px 15px rgba(0, 31, 92, 0.06)' : 'none')};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 820px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 1100px) {
    height: 600px;
  }
  @media (max-width: 768px) {
    height: 500px;
    padding: 10px;
  }
`

const TableCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const RangeSwitchWrapper = styled(SwitchWrapper)`
  background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : '#001842')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)')};
`

const RangeSwitchButton = styled(SwitchButton)`
  padding: 6px 14px;
  font-size: 13px;
`

const TableActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 220px;
  max-width: 320px;
  flex: 1;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`

const SearchInput = styled.input`
  width: 100%;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : '#001842')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)')};
  border-radius: 8px;
  padding: 7px 32px 7px 34px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  outline: none;
  transition: all 0.2s ease;
  font-family: inherit;

  &::placeholder {
    color: ${(props) => (props.theme.name === 'light' ? '#94a3b8' : '#64748b')};
  }

  &:focus {
    border-color: #a6c10b;
    box-shadow: 0 0 0 2px rgba(166, 193, 11, 0.2);
  }
`

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 11px;
  color: #64748b;
  font-size: 13px;
  pointer-events: none;
`

const ClearSearchButton = styled.button`
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: color 0.2s;

  &:hover {
    color: #ef4444;
  }
`

const ExportExcelButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
  font-family: inherit;

  &:hover {
    background: linear-gradient(135deg, #059669, #047857);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 15px;
  }
`

const ExportPdfButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.25);
  font-family: inherit;

  &:hover {
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 15px;
  }
`

const TableTitle = styled.h3`
  color: #a6c10b;
  margin-top: 0;
  margin-bottom: 0;
`

const ScrollArea = styled.div`
  overflow-y: auto;
  overflow-x: auto;
  flex: 1;
  min-height: 0;
  width: 100%;
  -webkit-overflow-scrolling: touch;

  scrollbar-color: #a6c10b transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    display: block;
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #a6c10b !important;
    border-radius: 10px !important;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #c2dc1b !important;
  }
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 6px 4px 6px;
  border-top: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  margin-top: 8px;
  flex-wrap: wrap;
  gap: 12px;
  user-select: none;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
`

const PaginationInfo = styled.div`
  font-size: 13px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 6px;

  strong {
    color: #a6c10b;
    font-weight: 600;
  }
`

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const PageButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ $active, theme }) => ($active ? '#a6c10b' : theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)')};
  background: ${({ $active, theme }) => ($active ? '#a6c10b' : theme.name === 'light' ? '#ffffff' : 'rgba(0, 24, 66, 0.6)')};
  color: ${({ $active, theme }) => ($active ? '#001842' : theme.name === 'light' ? '#0f172a' : '#ffffff')};

  &:hover:not(:disabled) {
    border-color: #a6c10b;
    background: ${({ $active }) => ($active ? '#c2dc1b' : 'rgba(166, 193, 11, 0.15)')};
    color: ${({ $active }) => ($active ? '#001842' : '#a6c10b')};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    border-color: rgba(255, 255, 255, 0.05);
  }

  svg {
    font-size: 12px;
  }
`

const PageEllipsis = styled.span`
  color: #94a3b8;
  font-size: 13px;
  padding: 0 4px;
`

const StyledTable = styled.table`
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  text-align: center;

  thead {
    position: sticky;
    top: 0;
    background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : '#082663')};
    z-index: 1;
  }
  th {
    padding: 12px 8px;
    font-weight: bold;
    border-bottom: 2px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : '#334155')};
    color: ${(props) => (props.theme.name === 'light' ? '#334155' : '#cbd5e1')};
    cursor: pointer;
    user-select: none;
    transition: color 0.2s;
    white-space: nowrap;
  }
  th div {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }
  th:hover {
    color: #a6c10b;
  }
  td {
    padding: 12px 8px;
    border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : '#1e293b')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    vertical-align: middle;
  }
  td:first-child {
    text-align: left;
  }
  th:first-child div {
    justify-content: flex-start;
  }
`

const TableRow = styled.tr<{ $isEven: boolean }>`
  background: ${(props) =>
    props.$isEven
      ? 'transparent'
      : props.theme.name === 'light'
      ? '#f8fafc'
      : '#0b3074'};
  transition: background 0.2s;

  &:hover {
    background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : '#113c8f')};
  }
`

const StatusText = styled.span<{ $alert?: boolean }>`
  color: ${(props) => (props.$alert ? '#ef4444' : props.theme.name === 'light' ? '#0f172a' : '#fff')};
`

const ValueText = styled.span`
  color: #a6c10b;
  font-weight: bold;
`

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: #a6c10b;
  cursor: pointer;
  font-size: 18px;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.2);
    color: #fff;
  }
`

const UnifiedPanelsGrid = styled.div<{ $singlePanel?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => (props.$singlePanel ? '1fr' : '1fr 1fr')};
  gap: 20px;
  width: 100%;
  margin-top: 4px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const UnifiedCard = styled.div`
  background: ${(props) => props.theme.cardBackground};
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 4px 15px rgba(0, 31, 92, 0.06)' : '0 4px 20px rgba(0, 0, 0, 0.25)')};
`

const UnifiedCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  padding-bottom: 12px;

  .title-area {
    display: flex;
    align-items: center;
    gap: 10px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }

    svg {
      color: #a6c10b;
      font-size: 18px;
    }
  }

  .badge {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    background: rgba(166, 193, 11, 0.12);
    color: #a6c10b;
    border: 1px solid rgba(166, 193, 11, 0.25);
  }
`

const SubChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const SubChartBox = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 24, 66, 0.45)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.05)')};
  border-radius: 8px;
  padding: 12px 8px 8px 8px;
  display: flex;
  flex-direction: column;
  height: 330px;
`

const ChartScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  scrollbar-color: #a6c10b transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    display: block;
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #a6c10b !important;
    border-radius: 10px !important;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #c2dc1b !important;
  }

  > div {
    min-width: 450px;
  }
`

const SkeletonBase = styled.div`
  background: #1e293b;
  background-image: linear-gradient(to right, #1e293b 0%, #334155 20%, #1e293b 40%, #1e293b 100%);
  background-repeat: no-repeat;
  background-size: 800px 100%;
  animation: ${shimmer} 1.2s infinite linear;
  border-radius: 4px;
`
const SkeletonRow = styled(SkeletonBase)`
  width: 100%;
  height: 45px;
  margin-bottom: 8px;
`
const SkeletonChart = styled(SkeletonBase)`
  width: 100%;
  height: 340px;
  border-radius: 12px;
`

const ModalDetailsOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(4, 21, 63, 0.7);
  backdrop-filter: blur(5px);
  z-index: 9999999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const ModalDetailsBox = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : '#082663')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(166, 193, 11, 0.3)')};
  border-radius: 16px;
  padding: 28px;
  width: 950px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : '0 25px 50px -12px rgba(0, 0, 0, 0.7)')};
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  box-sizing: border-box;

  scrollbar-color: #a6c10b transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    display: block;
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #a6c10b !important;
    border-radius: 10px !important;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #c2dc1b !important;
  }
`

const CloseButton = styled.button`
  background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.1)')};
  position: absolute;
  right: 16px;
  top: 16px;
  font-size: 20px;
  border: none;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.8);
    color: #fff;
    transform: scale(1.1);
  }
`

const ModalDetailsHeader = styled.div`
  border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
  padding-bottom: 16px;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
  p {
    margin: 8px 0 0 0;
    font-size: 15px;
    color: #a6c10b;
    font-weight: 500;
  }
`

const InfoCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`

const ModalSectionTitle = styled.h4`
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  font-size: 16px;
  margin: 20px 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
  padding-bottom: 8px;
`

const ModalTableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  max-height: 380px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)')};
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 24, 66, 0.5)')};

  scrollbar-color: #a6c10b transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    display: block;
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #a6c10b !important;
    border-radius: 10px !important;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #c2dc1b !important;
  }
`

const ModalProductsTable = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;

  thead {
    position: sticky;
    top: 0;
    background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : '#001842')};
    z-index: 2;
  }
  th {
    padding: 10px 12px;
    font-weight: 600;
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
    border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
    white-space: nowrap;
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
    vertical-align: middle;
  }
  tr:hover {
    background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.03)')};
  }
`

const InfoCard = styled.div<{ $borderColor?: string }>`
  background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'transparent')};
  border-left: 4px solid ${(props) => props.$borderColor || '#3b82f6'};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.06)')};
  }
`

const IconWrapper = styled.div<{ $color?: string }>`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'transparent')};
  color: ${(props) => props.$color || (props.theme.name === 'light' ? '#0284c7' : '#fff')};
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .main-value {
    font-size: 16px;
    font-weight: 700;
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  }

  .sub-value {
    font-size: 13px;
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#cbd5e1')};
  }

  .highlight-green {
    color: ${(props) => (props.theme.name === 'light' ? '#15803d' : '#a6c10b')};
  }
  .highlight-red {
    color: #ef4444;
  }
`

type SortConfig = { key: string; direction: 'asc' | 'desc' } | null

// Extrator seguro de data que aceita diferentes aliases da API e formatos (YYYYMMDD, ISO, BR, etc)
const extrairData = (linha: any): Date | null => {
  if (!linha) return null
  const val =
    linha.ultima_data ??
    linha.ultima_compra ??
    linha.data_ultima_compra ??
    linha.max_data ??
    linha.data ??
    linha.data_venda ??
    linha.dt_venda ??
    linha.dt_ultima_compra ??
    linha.ultimaVenda ??
    null
  return parseSafeDate(val)
}

// Parser numérico robusto que aceita números e strings (inclusive padrão BR com vírgula)
const parseSafeNumber = (val: any): number => {
  if (val === null || val === undefined || val === '') return 0
  if (typeof val === 'number') return isNaN(val) ? 0 : val
  const s = String(val).trim()
  if (s.includes(',')) {
    const cleaned = s.replace(/\./g, '').replace(',', '.')
    const n = parseFloat(cleaned)
    return isNaN(n) ? 0 : n
  }
  const n = parseFloat(s)
  return isNaN(n) ? 0 : n
}

// Busca a primeira chave numérica válida em snake_case ou camelCase
const extrairNumero = (obj: any, chaves: string[]): number => {
  if (!obj) return 0
  for (const k of chaves) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') {
      const parsed = parseSafeNumber(obj[k])
      if (parsed !== 0 || obj[k] === 0 || obj[k] === '0') return parsed
    }
  }
  return 0
}

// Formata moeda inteira em BRL sem centavos/decimais (ex: R$ 18.241)
const formatCurrencySemDecimais = (val: any) => {
  const n = parseSafeNumber(val)
  if (!n) return '-'
  return Math.round(n).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

// Formata caixas como inteiro sem decimais (ex: 1000 cx, 658 cx)
const formatCaixasSemDecimais = (val: any) => {
  const n = parseSafeNumber(val)
  if (!n) return '-'
  return `${Math.round(n).toLocaleString('pt-BR')} cx`
}

interface ClientAlertProps {
  hideFamilyCharts?: boolean
}

export const ClientAlert: React.FC<ClientAlertProps> = ({ hideFamilyCharts = false }) => {
  const { startDate, endDate, distributorId, cliente, marca, categoria, operacao, uf } =
    useGeralClienteFilter()

  const [loading, setLoading] = useState(true)
  const [tableData, setTableData] = useState<any[]>([])

  // Estado para controlar qual PDV foi clicado para abrir o Modal
  const [modalPdvData, setModalPdvData] = useState<any | null>(null)
  const [modalLoading, setModalLoading] = useState<boolean>(false)
  const [modalDetails, setModalDetails] = useState<any | null>(null)

  const handleOpenModal = async (row: any) => {
    setModalPdvData(row)
    setModalLoading(true)
    setModalDetails(null)

    try {
      const params = {
        cliente: row.pdv,
        distId: distributorId || 'todos',
        familias: categoria ? [categoria] : [],
        marca: marca ? [marca] : [],
        regional: operacao ? [operacao] : [],
        uf: uf ? [uf] : [],
      }

      const { data } = await api.post('/rank/pdv/detalhes', params)

      // Suporta tanto resposta direta quanto encapsulada em .data
      const resolved = data?.data || data || {}
      setModalDetails(resolved)
    } catch (error: any) {
      console.error(
        '[ERRO /rank/pdv/detalhes] Falha ao carregar detalhes do PDV:',
        error?.response?.status,
        error?.message,
      )
    } finally {
      setModalLoading(false)
    }
  }

  const [rankingPdvAlto, setRankingPdvAlto] = useState<any[]>([])
  const [rankingPdvBaixo, setRankingPdvBaixo] = useState<any[]>([])
  const [rankingFamAlto, setRankingFamAlto] = useState<any[]>([])
  const [rankingFamBaixo, setRankingFamBaixo] = useState<any[]>([])
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'giroMedio', direction: 'desc' })
  const [faixaDias, setFaixaDias] = useState<'todos' | '30-' | '30+' | '60+' | '90+'>('30+')
  const [searchPdv, setSearchPdv] = useState<string>('')

  const filteredTableData = useMemo(() => {
    const term = searchPdv.trim().toLowerCase()
    return tableData.filter((row) => {
      // Filtro de pesquisa de texto por nome do PDV
      if (term && !(row.pdv || '').toLowerCase().includes(term)) {
        return false
      }
      return true
    })
  }, [tableData, searchPdv])

  const sortedTableData = useMemo(() => {
    const items = [...filteredTableData]
    if (sortConfig !== null) {
      items.sort((a, b) => {
        let aVal = a[sortConfig.key]
        let bVal = b[sortConfig.key]

        if (sortConfig.key === 'dataUltimaCompra') {
          aVal = a.timestampUltimaCompra ?? 0
          bVal = b.timestampUltimaCompra ?? 0
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return items
  }, [filteredTableData, sortConfig])

  const [currentPage, setCurrentPage] = useState<number>(1)
  const pageSize = 30

  // Reseta para a página 1 ao alterar a faixa de dias, termo de busca ou recarregar a lista base
  useEffect(() => {
    setCurrentPage(1)
  }, [faixaDias, searchPdv, tableData])

  const totalPages = Math.max(1, Math.ceil(sortedTableData.length / pageSize))

  // Garante que currentPage permaneça dentro dos limites válidos
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1)
    }
  }, [currentPage, totalPages])

  const paginatedTableData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return sortedTableData.slice(startIndex, startIndex + pageSize)
  }, [sortedTableData, currentPage, pageSize])

  const getPaginationItems = () => {
    const pages: (number | string)[] = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
      return pages
    }

    pages.push(1)

    if (currentPage > 3) {
      pages.push('...')
    }

    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (currentPage < totalPages - 2) {
      pages.push('...')
    }

    pages.push(totalPages)

    return pages
  }

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
    setCurrentPage(1)
  }

  const handleExportMainExcel = () => {
    try {
      if (!sortedTableData || sortedTableData.length === 0) {
        toast.warning('Não há dados disponíveis para exportação na faixa selecionada.')
        return
      }

      const headers = [
        'PDV / RAZÃO SOCIAL',
        'CNPJ PDV',
        'DISTRIBUIDOR',
        'ÚLTIMA COMPRA',
        'DIAS SEM COMPRA',
        'TOTAL CAIXAS',
        'TOTAL VALOR (R$)',
        'GIRO MÉDIO (CX/DIA)',
        'MAIOR PEDIDO (CX)',
        'MAIOR PEDIDO (R$)',
      ]

      const dataRows = sortedTableData.map((row) => [
        cellText(row.pdv || '-'),
        cellText(row.cnpjPdv || '-', { center: true }),
        cellText(row.distribuidor || '-'),
        cellText(row.dataUltimaCompra || '-', { center: true }),
        row.temDataValida ? cellNumber(row.diasSemCompra) : cellText('-', { center: true }),
        cellNumber(row.totalCaixas > 0 ? Math.round(row.totalCaixas) : 0),
        cellMoney(row.totalValor > 0 ? row.totalValor : 0),
        cellNumber(Number(row.giroMedio || 0), '#,##0.00'),
        cellNumber(row.maiorPedidoCaixas > 0 ? Math.round(row.maiorPedidoCaixas) : 0),
        cellMoney(row.maiorPedidoValor > 0 ? row.maiorPedidoValor : 0),
      ])

      const totalCaixas = sortedTableData.reduce((acc, r) => acc + (r.totalCaixas > 0 ? r.totalCaixas : 0), 0)
      const totalValor = sortedTableData.reduce((acc, r) => acc + (r.totalValor > 0 ? r.totalValor : 0), 0)

      const totalsRow = [
        cellTotalLabel('TOTAL GERAL'),
        cellTotalLabel(''),
        cellTotalLabel(''),
        cellTotalLabel(''),
        cellTotalLabel(''),
        cellTotalNumber(Math.round(totalCaixas)),
        cellTotalMoney(totalValor),
        cellTotalLabel(''),
        cellTotalLabel(''),
        cellTotalLabel(''),
      ]

      const faixaNome = (faixaDias || 'TODOS').replace(/[^a-zA-Z0-9]/g, '_').toUpperCase()
      const dateStr = new Date().toISOString().split('T')[0]
      const fileName = `Giro_Alertas_PDV_${faixaNome}_${dateStr}.xlsx`

      const wb = XLSX.utils.book_new()
      const ws = buildCorporateSheet({
        title: 'ALERTA DE CLIENTES - GIRO E RETENÇÃO DE PDVS',
        subtitle: 'AUDITORIA DE FREQUÊNCIA DE COMPRA E DIAS SEM PEDIDO',
        meta: `FAIXA: ${faixaDias || 'TODOS OS CLIENTES'} | TOTAL REGISTROS: ${sortedTableData.length}`,
        headers,
        dataRows,
        totalsRow,
      })

      XLSX.utils.book_append_sheet(wb, ws, `Giro_${faixaNome.slice(0, 25)}`)
      XLSX.writeFile(wb, fileName)
    } catch (error) {
      console.error('Erro ao exportar Excel:', error)
      toast.error('Ocorreu um erro ao gerar a planilha Excel. Por favor, tente novamente.')
    }
  }

  const handleExportModalPDF = () => {
    try {
      if (!modalPdvData) {
        toast.warning('Não há dados do cliente selecionado para exportação.')
        return
      }

      const clientInfo = modalDetails?.cliente || modalDetails?.data?.cliente || {}
      const maiorPedidoInfo =
        modalDetails?.maiorPedido ||
        modalDetails?.data?.maiorPedido ||
        modalDetails?.maior_pedido ||
        {}
      const produtosList =
        (Array.isArray(modalDetails?.produtos) && modalDetails.produtos) ||
        (Array.isArray(modalDetails?.data?.produtos) && modalDetails.data.produtos) ||
        []

      const nomeCliente = clientInfo?.nm_client || clientInfo?.razao_social || modalPdvData.pdv || 'Cliente'
      const cnpj = clientInfo?.cnpj_cpf_pdv ? String(clientInfo.cnpj_cpf_pdv) : '-'
      const ufCliente = clientInfo?.uf || modalPdvData.uf || '-'
      const dtUltimaCompra = clientInfo?.ultima_compra ? formatDateBr(clientInfo.ultima_compra) : modalPdvData.dataUltimaCompra || '-'
      const diasSemCompra = clientInfo?.dias_sem_compra !== undefined ? clientInfo.dias_sem_compra : modalPdvData.diasSemCompra

      const mpCaixas =
        extrairNumero(maiorPedidoInfo, ['total_caixas', 'totalCaixas', 'caixas', 'cx']) ||
        modalPdvData.maiorPedidoCaixas ||
        0
      const mpDist = maiorPedidoInfo?.distribuidor || maiorPedidoInfo?.dist || '-'
      const mpData = maiorPedidoInfo?.data ? formatDateBr(maiorPedidoInfo.data) : '-'

      const totalHist =
        extrairNumero(clientInfo, [
          'total_caixas_historico',
          'totalCaixasHistorico',
          'total_caixas',
          'totalCaixas',
        ]) ||
        modalPdvData.totalCaixas ||
        0
      const primeiraCompra = clientInfo?.primeira_compra ? formatDateBr(clientInfo.primeira_compra) : '-'

      const doc = new (jsPDF as any)('l', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()

      // Fundo azul marinho GID
      doc.setFillColor(0, 29, 83)
      doc.rect(0, 0, pageWidth, pageHeight, 'F')

      // Faixa decorativa verde limão GID
      doc.setFillColor(166, 193, 11)
      doc.rect(0, 0, pageWidth, 4, 'F')

      // Cabeçalho Principal
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(16)
      doc.setTextColor(166, 193, 11)
      doc.text('FICHA DE DETALHAMENTO DO PDV', 14, 16)

      doc.setFontSize(13)
      doc.setTextColor(255, 255, 255)
      doc.text(nomeCliente.toUpperCase(), 14, 23)

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.setTextColor(148, 163, 184)
      doc.text(`CNPJ/CPF: ${cnpj}   |   UF: ${ufCliente}`, 14, 29)

      const agora = new Date()
      const dataEmissao = `${agora.toLocaleDateString('pt-BR')} às ${agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
      doc.setFontSize(9)
      doc.text(`Emissão: ${dataEmissao}`, pageWidth - 14, 29, { align: 'right' })

      doc.setDrawColor(30, 64, 130)
      doc.setLineWidth(0.5)
      doc.line(14, 32, pageWidth - 14, 32)

      // 3 Cards de Resumo Lado a Lado
      const cardWidth = (pageWidth - 28 - 16) / 3
      const cardHeight = 22
      const cardY = 36

      // Card 1: Última Movimentação
      doc.setFillColor(10, 37, 94)
      doc.roundedRect(14, cardY, cardWidth, cardHeight, 2, 2, 'F')
      doc.setDrawColor(59, 130, 246)
      doc.setLineWidth(0.5)
      doc.roundedRect(14, cardY, cardWidth, cardHeight, 2, 2, 'S')

      doc.setFontSize(8)
      doc.setTextColor(148, 163, 184)
      doc.text('ÚLTIMA MOVIMENTAÇÃO', 18, cardY + 6)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor(255, 255, 255)
      doc.text(String(dtUltimaCompra), 18, cardY + 12)
      doc.setFontSize(8)
      if (diasSemCompra !== null && diasSemCompra !== undefined && !isNaN(diasSemCompra)) {
        if (diasSemCompra > 20) {
          doc.setTextColor(239, 68, 68)
        } else {
          doc.setTextColor(166, 193, 11)
        }
        doc.text(`Inativo há ${diasSemCompra} dias`, 18, cardY + 18)
      } else {
        doc.setTextColor(148, 163, 184)
        doc.text('Sem compras no período', 18, cardY + 18)
      }

      // Card 2: Maior Pedido Realizado
      const card2X = 14 + cardWidth + 8
      doc.setFillColor(10, 37, 94)
      doc.roundedRect(card2X, cardY, cardWidth, cardHeight, 2, 2, 'F')
      doc.setDrawColor(166, 193, 11)
      doc.setLineWidth(0.5)
      doc.roundedRect(card2X, cardY, cardWidth, cardHeight, 2, 2, 'S')

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(148, 163, 184)
      doc.text('MAIOR PEDIDO REALIZADO', card2X + 4, cardY + 6)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor(166, 193, 11)
      doc.text(mpCaixas > 0 ? `${Math.round(mpCaixas).toLocaleString('pt-BR')} cx` : '-', card2X + 4, cardY + 12)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(148, 163, 184)
      const subTextMp = mpDist !== '-' ? `${mpDist}${mpData !== '-' ? ` • ${mpData}` : ''}` : 'Maior compra registrada'
      doc.text(subTextMp.length > 38 ? subTextMp.substring(0, 38) + '...' : subTextMp, card2X + 4, cardY + 18)

      // Card 3: Histórico Total
      const card3X = card2X + cardWidth + 8
      doc.setFillColor(10, 37, 94)
      doc.roundedRect(card3X, cardY, cardWidth, cardHeight, 2, 2, 'F')
      doc.setDrawColor(245, 158, 11)
      doc.setLineWidth(0.5)
      doc.roundedRect(card3X, cardY, cardWidth, cardHeight, 2, 2, 'S')

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(148, 163, 184)
      doc.text('HISTÓRICO TOTAL', card3X + 4, cardY + 6)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor(245, 158, 11)
      doc.text(totalHist > 0 ? `${Math.round(totalHist).toLocaleString('pt-BR')} cx` : '-', card3X + 4, cardY + 12)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(148, 163, 184)
      doc.text(primeiraCompra !== '-' ? `Cliente desde ${primeiraCompra}` : 'Volume acumulado histórico', card3X + 4, cardY + 18)

      // Título da Tabela de Produtos
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(12)
      doc.setTextColor(166, 193, 11)
      doc.text('PRODUTOS COMPRADOS PELO CLIENTE', 14, 65)

      const tableBody = produtosList.map((prod: any) => {
        const dt = extrairData(prod)
        const dias = prod.dias_sem_compra !== undefined ? Number(prod.dias_sem_compra) : null
        const prodCx = extrairNumero(prod, ['total_caixas', 'totalCaixas', 'total_cx', 'caixas', 'cx'])
        const prodMaiorCx = extrairNumero(prod, ['maior_pedido_caixas', 'maiorPedidoCaixas', 'maior_caixas', 'maiorCaixas', 'max_caixas'])

        return [
          prod.produto || '-',
          prod.descricao || prod.produto || 'Produto Sem Descrição',
          prod.familia || '-',
          prod.marca || '-',
          prodCx > 0 ? `${Math.round(prodCx).toLocaleString('pt-BR')} cx` : '-',
          prodMaiorCx > 0 ? `${Math.round(prodMaiorCx).toLocaleString('pt-BR')} cx` : '-',
          dt ? formatDateBr(dt) : '-',
          dias !== null && !isNaN(dias) ? `${dias} dias` : '-',
        ]
      })

      autoTable(doc, {
        startY: 68,
        head: [
          [
            'Código',
            'Produto / Descrição',
            'Família',
            'Marca',
            'Total Caixas',
            'Maior Pedido',
            'Última Venda',
            'Dias s/ Compra',
          ],
        ],
        body:
          tableBody.length > 0
            ? tableBody
            : [['-', 'Nenhum produto individual retornado para este cliente.', '-', '-', '-', '-', '-', '-']],
        headStyles: {
          fillColor: [8, 38, 99],
          textColor: [166, 193, 11],
          fontStyle: 'bold',
          fontSize: 8.5,
          halign: 'left',
        },
        bodyStyles: {
          fillColor: [0, 29, 83],
          textColor: [255, 255, 255],
          fontSize: 8,
          lineColor: [20, 50, 110],
          lineWidth: 0.1,
        },
        alternateRowStyles: {
          fillColor: [4, 33, 90],
        },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 85 },
          2: { cellWidth: 32 },
          3: { cellWidth: 30 },
          4: { cellWidth: 26, halign: 'center' },
          5: { cellWidth: 26, halign: 'center' },
          6: { cellWidth: 22, halign: 'center' },
          7: { cellWidth: 20, halign: 'center' },
        },
        margin: { left: 14, right: 14, bottom: 14 },
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) {
            doc.setFillColor(0, 29, 83)
            doc.rect(0, 0, pageWidth, pageHeight, 'F')
          }
          const pageCount = typeof doc.getNumberOfPages === 'function' ? doc.getNumberOfPages() : data.pageNumber
          doc.setFontSize(8)
          doc.setTextColor(148, 163, 184)
          doc.text(
            `GID - Gestão de Informações de Distribuidores   |   Página ${data.pageNumber} de ${pageCount}`,
            pageWidth / 2,
            pageHeight - 6,
            { align: 'center' },
          )
        },
      })

      const dateStr = agora.toISOString().split('T')[0]
      const nomeLimpo = (nomeCliente || 'Cliente').replace(/[^a-zA-Z0-9]/g, '_')
      const fileName = `Ficha_PDV_${nomeLimpo}_${dateStr}.pdf`

      const blob = doc.output('blob')
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 100)
    } catch (error) {
      console.error('Erro ao exportar PDF:', error)
      toast.error('Ocorreu um erro ao gerar o PDF. Por favor, tente novamente.')
    }
  }

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const now = new Date()
      const tresMesesAtras = new Date()
      tresMesesAtras.setMonth(now.getMonth() - 3)

      const payloadStartDate = startDate
        ? normalizeDateForComparison(startDate)
        : normalizeDateForComparison(tresMesesAtras)
      const payloadEndDate = endDate
        ? normalizeDateForComparison(endDate)
        : normalizeDateForComparison(now)

      const params = {
        startDate: payloadStartDate,
        endDate: payloadEndDate,
        distId: distributorId || 'todos',
        cliente: cliente || '',
        familias: categoria ? [categoria] : [],
        marca: marca ? [marca] : [],
        regional: operacao ? [operacao] : [],
        uf: uf ? [uf] : [],
        dias: faixaDias,
      }

      // Os três agrupamentos vêm prontos do SQL: total e última compra por
      // PDV, o mesmo por produto dentro de cada PDV, e o total por família.
      // Antes vinha o sellout linha a linha e a tela montava três Maps
      // aninhados em memória.
      const { data } = await api.post('/rank/pdv', params)

      const linhas = (chave: string): any[] =>
        Array.isArray(data?.[chave]) ? data[chave] : []

      const today = new Date()

      // Pior item e último item comprado de cada PDV, a partir do agrupamento
      // por PDV × produto.
      const detalhesPorPdv = new Map<string, { pior: any; ultimo: any }>()
      linhas('porPdvProduto').forEach((linha) => {
        const pdv = linha.nm_client || 'Cliente Desconhecido'
        const volume = Number(linha.total_caixas) || 0
        const dataProd = extrairData(linha)

        if (!detalhesPorPdv.has(pdv)) {
          detalhesPorPdv.set(pdv, {
            pior: { name: '-', volume: Infinity },
            ultimo: { name: '-', data: null },
          })
        }
        const det = detalhesPorPdv.get(pdv)!
        const nome = linha.produto || 'Produto Não Identificado'

        if (volume > 0 && volume < det.pior.volume) det.pior = { name: nome, volume }
        if (dataProd && (!det.ultimo.data || dataProd > det.ultimo.data)) {
          det.ultimo = { name: nome, data: dataProd }
        }
      })

      const formattedTable = linhas('porPdv').map((linha, index) => {
        const pdv = linha.nm_client || 'Cliente Desconhecido'
        const ultimaCompra = extrairData(linha)
        const det = detalhesPorPdv.get(pdv)

        let dataUltimaCompra = '-'
        let diasSemCompra = 0
        let timestampUltimaCompra = 0
        let temDataValida = false

        if (ultimaCompra) {
          dataUltimaCompra = formatDateBr(ultimaCompra)
          timestampUltimaCompra = ultimaCompra.getTime()
          temDataValida = true
        }

        if (
          linha.dias_sem_compra !== undefined &&
          linha.dias_sem_compra !== null &&
          !isNaN(Number(linha.dias_sem_compra))
        ) {
          diasSemCompra = Number(linha.dias_sem_compra)
          temDataValida = true
        } else if (ultimaCompra) {
          const diffTime = today.getTime() - ultimaCompra.getTime()
          diasSemCompra = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)))
          temDataValida = true
        }

        const totalCaixas = Math.round(
          extrairNumero(linha, [
            'total_caixas',
            'totalCaixas',
            'total_cx',
            'totalCx',
            'caixas',
            'cx',
          ]),
        )

        const totalValor = Math.round(
          extrairNumero(linha, [
            'total_valor',
            'totalValor',
            'valor_total',
            'valorTotal',
            'vl_total',
            'vlTotal',
            'valor',
            'total',
          ]),
        )

        let maiorPedidoCaixas = Math.round(
          extrairNumero(linha, [
            'maior_pedido_caixas',
            'maiorPedidoCaixas',
            'maior_pedido',
            'maiorPedido',
            'max_pedido_caixas',
            'maxPedidoCaixas',
            'maior_caixas',
            'maiorCaixas',
            'max_caixas',
            'maxCaixas',
          ]),
        )

        let maiorPedidoValor = Math.round(
          extrairNumero(linha, [
            'maior_pedido_valor',
            'maiorPedidoValor',
            'maior_valor',
            'maiorValor',
            'max_pedido_valor',
            'maxPedidoValor',
            'max_valor',
            'maxValor',
            'maior_venda',
            'maiorVenda',
            'max_venda',
            'maxVenda',
          ]),
        )

        const mpObj = linha.maiorPedido || linha.maior_pedido
        if (typeof mpObj === 'object' && mpObj !== null) {
          if (!maiorPedidoCaixas) {
            maiorPedidoCaixas = Math.round(
              extrairNumero(mpObj, ['total_caixas', 'totalCaixas', 'caixas', 'cx']),
            )
          }
          if (!maiorPedidoValor) {
            maiorPedidoValor = Math.round(
              extrairNumero(mpObj, ['valor', 'total_valor', 'totalValor', 'vl_total']),
            )
          }
        }

        const cnpjPdv =
          linha.cnpj_cpf_pdv ||
          linha.cnpj ||
          linha.cpf ||
          linha.cnpj_pdv ||
          linha.cnpj_cpf ||
          linha.cnpjCpfPdv ||
          linha.cnpjPdv ||
          linha.cod_cliente ||
          linha.codigo_pdv ||
          '-'

        const distribuidor =
          linha.distribuidor ||
          linha.nm_dist ||
          linha.dist ||
          linha.nome_dist ||
          linha.nome_distribuidor ||
          linha.nm_distribuidor ||
          linha.razao_social_distribuidor ||
          linha.distribuidor_nome ||
          '-'

        return {
          id: index,
          pdv,
          cnpjPdv,
          distribuidor,
          dataUltimaCompra,
          diasSemCompra,
          timestampUltimaCompra,
          temDataValida,
          totalCaixas,
          totalValor,
          giroMedio: totalCaixas / 90,
          maiorPedidoCaixas,
          maiorPedidoValor,
          detalhesModal: {
            piorProduto: !det || det.pior.volume === Infinity ? 'N/A' : det.pior.name,
            piorVolume: !det || det.pior.volume === Infinity ? 0 : det.pior.volume,
            ultimoProduto: !det || !det.ultimo.data || det.ultimo.name === '-' ? 'N/A' : det.ultimo.name,
          },
        }
      })

      setTableData(formattedTable)

      if (Array.isArray(data?.top10PdvAlto) && data.top10PdvAlto.length > 0) {
        setRankingPdvAlto(data.top10PdvAlto)
      } else {
        const sortedPdvs = [...formattedTable].sort((a, b) => b.giroMedio - a.giroMedio)
        setRankingPdvAlto(
          sortedPdvs.slice(0, 10).map((i) => ({ name: i.pdv, giroMedio: i.giroMedio })),
        )
      }

      if (Array.isArray(data?.top10PdvBaixo) && data.top10PdvBaixo.length > 0) {
        setRankingPdvBaixo(data.top10PdvBaixo)
      } else {
        const sortedPdvs = [...formattedTable].sort((a, b) => a.giroMedio - b.giroMedio)
        setRankingPdvBaixo(
          sortedPdvs.slice(0, 10).map((i) => ({ name: i.pdv, giroMedio: i.giroMedio })),
        )
      }

      if (Array.isArray(data?.top10FamAlto) && data.top10FamAlto.length > 0) {
        setRankingFamAlto(data.top10FamAlto)
      } else {
        const sortedFamilias = linhas('porFamilia')
          .map((fam) => ({
            name: fam.familia || 'Sem Família',
            giroMedio: (Number(fam.total_caixas) || 0) / 90,
          }))
          .sort((a, b) => b.giroMedio - a.giroMedio)

        setRankingFamAlto(sortedFamilias.slice(0, 10))
      }

      if (Array.isArray(data?.top10FamBaixo) && data.top10FamBaixo.length > 0) {
        setRankingFamBaixo(data.top10FamBaixo)
      } else {
        const sortedFamilias = linhas('porFamilia')
          .map((fam) => ({
            name: fam.familia || 'Sem Família',
            giroMedio: (Number(fam.total_caixas) || 0) / 90,
          }))
          .sort((a, b) => a.giroMedio - b.giroMedio)

        setRankingFamBaixo(sortedFamilias.slice(0, 10))
      }
    } catch (error) {
      console.error('Erro ao carregar:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDashboardData()
  }, [startDate, endDate, distributorId, cliente, marca, categoria, operacao, uf, faixaDias])

  return (
    <PageContainer>
      {modalPdvData && (
        <ModalDetailsOverlay
          onClick={() => {
            setModalPdvData(null)
            setModalDetails(null)
          }}
        >
          <ModalDetailsBox onClick={(e) => e.stopPropagation()}>
            <CloseButton
              onClick={() => {
                setModalPdvData(null)
                setModalDetails(null)
              }}
            >
              <IoCloseCircleSharp />
            </CloseButton>

            <ModalDetailsHeader>
              <h3>Detalhamento do Cliente</h3>
              <p>
                {modalDetails?.cliente?.nm_client ||
                  modalDetails?.cliente?.razao_social ||
                  modalPdvData.pdv}
                {modalDetails?.cliente?.cnpj_cpf_pdv ? ` • CNPJ/CPF: ${modalDetails.cliente.cnpj_cpf_pdv}` : ''}
                {modalDetails?.cliente?.uf ? ` (${modalDetails.cliente.uf})` : ''}
              </p>
            </ModalDetailsHeader>

            {modalLoading ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '260px',
                  gap: '16px',
                }}
              >
                <ThreeDot variant='bounce' color='#a6c10b' size='medium' />
                <span style={{ color: '#94a3b8', fontSize: '14px' }}>
                  Carregando dados detalhados do cliente...
                </span>
              </div>
            ) : (
              (() => {
                const clientInfo = modalDetails?.cliente || modalDetails?.data?.cliente || {}
                const maiorPedidoInfo =
                  modalDetails?.maiorPedido ||
                  modalDetails?.data?.maiorPedido ||
                  modalDetails?.maior_pedido ||
                  {}
                const produtosList =
                  (Array.isArray(modalDetails?.produtos) && modalDetails.produtos) ||
                  (Array.isArray(modalDetails?.data?.produtos) && modalDetails.data.produtos) ||
                  []

                const mpCaixas =
                  extrairNumero(maiorPedidoInfo, ['total_caixas', 'totalCaixas', 'caixas', 'cx']) ||
                  modalPdvData.maiorPedidoCaixas ||
                  0

                const totalHist =
                  extrairNumero(clientInfo, [
                    'total_caixas_historico',
                    'totalCaixasHistorico',
                    'total_caixas',
                    'totalCaixas',
                  ]) ||
                  modalPdvData.totalCaixas ||
                  0

                return (
                  <>
                    <InfoCardGrid>
                      {/* Card 1: Última Compra */}
                      <InfoCard $borderColor='#3b82f6'>
                        <IconWrapper $color='#3b82f6'>
                          <FaCalendarAlt />
                        </IconWrapper>
                        <InfoContent>
                          <label>Última Movimentação</label>
                          <span className='main-value'>
                            {clientInfo?.ultima_compra
                              ? formatDateBr(clientInfo.ultima_compra)
                              : modalPdvData.dataUltimaCompra}
                          </span>
                          <span className='sub-value'>
                            {clientInfo?.dias_sem_compra !== undefined ? (
                              <>
                                Inativo há{' '}
                                <strong
                                  className={clientInfo.dias_sem_compra > 20 ? 'highlight-red' : ''}
                                >
                                  {clientInfo.dias_sem_compra} dias
                                </strong>
                              </>
                            ) : modalPdvData.temDataValida ? (
                              <>
                                Inativo há{' '}
                                <strong className={modalPdvData.diasSemCompra > 20 ? 'highlight-red' : ''}>
                                  {modalPdvData.diasSemCompra} dias
                                </strong>
                              </>
                            ) : (
                              <span>Sem compras no período</span>
                            )}
                          </span>
                        </InfoContent>
                      </InfoCard>

                      {/* Card 2: Maior Pedido */}
                      <InfoCard $borderColor='#a6c10b'>
                        <IconWrapper $color='#a6c10b'>
                          <FaStar />
                        </IconWrapper>
                        <InfoContent>
                          <label>Maior Pedido Realizado</label>
                          <span className='main-value highlight-green'>
                            {mpCaixas > 0 ? formatCaixasSemDecimais(mpCaixas) : '-'}
                          </span>
                          <span className='sub-value'>
                            {maiorPedidoInfo?.distribuidor ? (
                              <span>
                                {maiorPedidoInfo.distribuidor}
                                {maiorPedidoInfo.data
                                  ? ` • ${formatDateBr(maiorPedidoInfo.data)}`
                                  : ''}
                              </span>
                            ) : (
                              <span>Maior compra registrada</span>
                            )}
                          </span>
                        </InfoContent>
                      </InfoCard>

                      {/* Card 3: Histórico Total */}
                      <InfoCard $borderColor='#f59e0b'>
                        <IconWrapper $color='#f59e0b'>
                          <FaBoxes />
                        </IconWrapper>
                        <InfoContent>
                          <label>Histórico Total</label>
                          <span className='main-value' style={{ color: '#f59e0b' }}>
                            {totalHist > 0 ? formatCaixasSemDecimais(totalHist) : '-'}
                          </span>
                          <span className='sub-value'>
                            {clientInfo?.primeira_compra ? (
                              <span>Cliente desde {formatDateBr(clientInfo.primeira_compra)}</span>
                            ) : (
                              <span>Volume acumulado histórico</span>
                            )}
                          </span>
                        </InfoContent>
                      </InfoCard>
                    </InfoCardGrid>

                    {/* Seção Tabela de Produtos */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        margin: '22px 0 12px',
                        flexWrap: 'wrap',
                        gap: '10px',
                      }}
                    >
                      <ModalSectionTitle style={{ margin: 0 }}>
                        <FaBoxes style={{ color: '#a6c10b' }} />
                        Produtos Comprados pelo Cliente
                      </ModalSectionTitle>
                      <ExportPdfButton onClick={handleExportModalPDF} title='Baixar Ficha do Cliente em PDF'>
                        <FaFilePdf />
                        Exportar Ficha (PDF)
                      </ExportPdfButton>
                    </div>

                    <ModalTableContainer>
                      <ModalProductsTable>
                        <thead>
                          <tr>
                            <th>Produto / Código</th>
                            <th>Família / Marca</th>
                            <th>Total Caixas</th>
                            <th>Maior Pedido (Cx)</th>
                            <th>Última Venda</th>
                            <th>Dias Sem Compra</th>
                          </tr>
                        </thead>
                        <tbody>
                          {produtosList.length > 0 ? (
                            produtosList.map((prod: any, idx: number) => {
                              const dt = extrairData(prod)
                              const dias =
                                prod.dias_sem_compra !== undefined ? Number(prod.dias_sem_compra) : null
                              const prodCx = extrairNumero(prod, [
                                'total_caixas',
                                'totalCaixas',
                                'total_cx',
                                'caixas',
                                'cx',
                              ])
                              const prodMaiorCx = extrairNumero(prod, [
                                'maior_pedido_caixas',
                                'maiorPedidoCaixas',
                                'maior_caixas',
                                'maiorCaixas',
                                'max_caixas',
                              ])

                              return (
                                <tr key={prod.produto || idx}>
                                  <td>
                                    <div style={{ fontWeight: 600, color: '#fff' }}>
                                      {prod.descricao || prod.produto || 'Produto Sem Descrição'}
                                    </div>
                                    {prod.descricao && prod.produto && (
                                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                                        Cód: {prod.produto}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <div>{prod.familia || '-'}</div>
                                    {prod.marca && (
                                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                        {prod.marca}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ color: '#a6c10b', fontWeight: 600 }}>
                                    {prodCx > 0 ? formatCaixasSemDecimais(prodCx) : '-'}
                                  </td>
                                  <td style={{ color: '#38bdf8', fontWeight: 600 }}>
                                    {prodMaiorCx > 0 ? formatCaixasSemDecimais(prodMaiorCx) : '-'}
                                  </td>
                                  <td>{dt ? formatDateBr(dt) : '-'}</td>
                                  <td>
                                    {dias !== null && !isNaN(dias) ? (
                                      <StatusText $alert={dias > 20}>{dias} dias</StatusText>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </td>
                                </tr>
                              )
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={6}
                                style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}
                              >
                                Nenhum produto individual retornado para este cliente com os filtros atuais.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </ModalProductsTable>
                    </ModalTableContainer>
                  </>
                )
              })()
            )}
          </ModalDetailsBox>
        </ModalDetailsOverlay>
      )}

      <Header>
        <div style={{ borderLeft: '4px solid #a6c10b', paddingLeft: '10px' }}>
          <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 700 }}>Acompanhamento de Giro</h2>
          <p style={{ fontSize: '12px', margin: 0 }}>
            Leitura de giro de sellout
          </p>
        </div>
      </Header>

      <DashboardGrid>
        {loading ? (
          <>
            <TableCard>
              <TableTitle>Carregando Análise...</TableTitle>
              {[...Array(12)].map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </TableCard>
            <UnifiedPanelsGrid $singlePanel={hideFamilyCharts}>
              <UnifiedCard>
                <UnifiedCardHeader>
                  <div className='title-area'>
                    <FaStore />
                    <h3>Desempenho por PDV (Clientes)</h3>
                  </div>
                </UnifiedCardHeader>
                <SubChartsGrid>
                  <SkeletonChart style={{ height: '330px' }} />
                  <SkeletonChart style={{ height: '330px' }} />
                </SubChartsGrid>
              </UnifiedCard>

              {!hideFamilyCharts && (
                <UnifiedCard>
                  <UnifiedCardHeader>
                    <div className='title-area'>
                      <FaBoxes />
                      <h3>Desempenho por Família (Categorias)</h3>
                    </div>
                  </UnifiedCardHeader>
                  <SubChartsGrid>
                    <SkeletonChart style={{ height: '330px' }} />
                    <SkeletonChart style={{ height: '330px' }} />
                  </SubChartsGrid>
                </UnifiedCard>
              )}
            </UnifiedPanelsGrid>
          </>
        ) : (
          <>
            <TableCard>
              <TableCardHeader>
                <TableTitle>Análise de Alertas e Giros</TableTitle>
                <TableActionsWrapper>
                  <SearchWrapper>
                    <SearchIcon />
                    <SearchInput
                      type="text"
                      placeholder="Buscar PDV..."
                      value={searchPdv}
                      onChange={(e) => setSearchPdv(e.target.value)}
                    />
                    {searchPdv && (
                      <ClearSearchButton onClick={() => setSearchPdv('')} title="Limpar busca">
                        <FaTimes />
                      </ClearSearchButton>
                    )}
                  </SearchWrapper>

                  <RangeSwitchWrapper>
                    <RangeSwitchButton $active={faixaDias === 'todos'} onClick={() => setFaixaDias('todos')}>
                      Todos
                    </RangeSwitchButton>
                    <RangeSwitchButton $active={faixaDias === '30-'} onClick={() => setFaixaDias('30-')}>
                      30-
                    </RangeSwitchButton>
                    <RangeSwitchButton $active={faixaDias === '30+'} onClick={() => setFaixaDias('30+')}>
                      30+
                    </RangeSwitchButton>
                    <RangeSwitchButton $active={faixaDias === '60+'} onClick={() => setFaixaDias('60+')}>
                      60+
                    </RangeSwitchButton>
                    <RangeSwitchButton $active={faixaDias === '90+'} onClick={() => setFaixaDias('90+')}>
                      90+
                    </RangeSwitchButton>
                  </RangeSwitchWrapper>

                  <ExportExcelButton onClick={handleExportMainExcel} title='Exportar dados da tabela atual em Excel'>
                    <FaFileExcel />
                    Exportar Excel
                  </ExportExcelButton>
                </TableActionsWrapper>
              </TableCardHeader>
              <ScrollArea>
                <StyledTable>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('pdv')}>
                        <div>
                          PDV{' '}
                          {sortConfig?.key === 'pdv' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      <th onClick={() => handleSort('dataUltimaCompra')}>
                        <div>
                          Última Compra{' '}
                          {sortConfig?.key === 'dataUltimaCompra' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      <th onClick={() => handleSort('diasSemCompra')}>
                        <div>
                          Dias Sem Compra{' '}
                          {sortConfig?.key === 'diasSemCompra' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      <th onClick={() => handleSort('totalCaixas')}>
                        <div>
                          Total Caixas{' '}
                          {sortConfig?.key === 'totalCaixas' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      <th onClick={() => handleSort('totalValor')}>
                        <div>
                          Total Valor{' '}
                          {sortConfig?.key === 'totalValor' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      <th onClick={() => handleSort('giroMedio')}>
                        <div>
                          Giro Médio{' '}
                          {sortConfig?.key === 'giroMedio' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      <th onClick={() => handleSort('maiorPedidoCaixas')}>
                        <div>
                          Maior Pedido (Cx){' '}
                          {sortConfig?.key === 'maiorPedidoCaixas' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      <th onClick={() => handleSort('maiorPedidoValor')}>
                        <div>
                          Maior Pedido (R$){' '}
                          {sortConfig?.key === 'maiorPedidoValor' &&
                            (sortConfig.direction === 'asc' ? (
                              <FaCircleChevronUp />
                            ) : (
                              <FaCircleChevronDown />
                            ))}
                        </div>
                      </th>
                      {/* Nova Coluna para o Botão */}
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTableData.length > 0 ? (
                      paginatedTableData.map((row, index) => (
                        <TableRow key={row.id} $isEven={index % 2 === 0}>
                          <td>{row.pdv}</td>
                          <td>{row.dataUltimaCompra}</td>
                          <td>
                            <StatusText $alert={row.temDataValida && row.diasSemCompra > 20}>
                              {row.temDataValida ? `${row.diasSemCompra} dias` : '-'}
                            </StatusText>
                          </td>
                          <td>
                            <ValueText>
                              {row.totalCaixas > 0 ? formatCaixasSemDecimais(row.totalCaixas) : '-'}
                            </ValueText>
                          </td>
                          <td>
                            <span style={{ color: '#38bdf8', fontWeight: 600 }}>
                              {row.totalValor > 0 ? formatCurrencySemDecimais(row.totalValor) : '-'}
                            </span>
                          </td>
                          <td>
                            <ValueText>{row.giroMedio.toFixed(2).replace('.', ',')} cx/dia</ValueText>
                          </td>
                          <td>
                            <ValueText>
                              {row.maiorPedidoCaixas > 0
                                ? formatCaixasSemDecimais(row.maiorPedidoCaixas)
                                : '-'}
                            </ValueText>
                          </td>
                          <td>
                            <span style={{ color: '#38bdf8', fontWeight: 600 }}>
                              {row.maiorPedidoValor > 0
                                ? formatCurrencySemDecimais(row.maiorPedidoValor)
                                : '-'}
                            </span>
                          </td>
                          <td>
                            <ActionButton
                              title='Ver Detalhes do Cliente'
                              onClick={() => handleOpenModal(row)}
                            >
                              <FaEye />
                            </ActionButton>
                          </td>
                        </TableRow>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} style={{ textAlign: 'center', padding: '40px 10px', color: '#94a3b8' }}>
                          {searchPdv
                            ? `Nenhum PDV encontrado para "${searchPdv}".`
                            : 'Nenhum PDV encontrado para os filtros selecionados.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </StyledTable>
              </ScrollArea>

              <PaginationContainer>
                <PaginationInfo>
                  {sortedTableData.length > 0 ? (
                    <>
                      Mostrando <strong>{((currentPage - 1) * pageSize) + 1}</strong> a{' '}
                      <strong>{Math.min(currentPage * pageSize, sortedTableData.length)}</strong> de{' '}
                      <strong>{sortedTableData.length}</strong> PDVs (Página{' '}
                      <strong>{currentPage}</strong> de <strong>{totalPages}</strong>)
                    </>
                  ) : (
                    'Nenhum PDV encontrado'
                  )}
                </PaginationInfo>

                {totalPages > 1 && (
                  <PaginationControls>
                    <PageButton
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      title='Primeira página'
                    >
                      <FaAnglesLeft />
                    </PageButton>
                    <PageButton
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      title='Página anterior'
                    >
                      <FaChevronLeft />
                    </PageButton>

                    {getPaginationItems().map((item, idx) => {
                      if (typeof item === 'string') {
                        return <PageEllipsis key={`ellipsis-${idx}`}>...</PageEllipsis>
                      }
                      return (
                        <PageButton
                          key={item}
                          $active={item === currentPage}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </PageButton>
                      )
                    })}

                    <PageButton
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      title='Próxima página'
                    >
                      <FaChevronRight />
                    </PageButton>
                    <PageButton
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      title='Última página'
                    >
                      <FaAnglesRight />
                    </PageButton>
                  </PaginationControls>
                )}
              </PaginationContainer>
            </TableCard>

            <UnifiedPanelsGrid $singlePanel={hideFamilyCharts}>
              <UnifiedCard>
                <UnifiedCardHeader>
                  <div className='title-area'>
                    <FaStore />
                    <h3>Desempenho por PDV (Clientes)</h3>
                  </div>
                </UnifiedCardHeader>
                <SubChartsGrid>
                  <SubChartBox>
                    <RankSubChart title='Top 10 Desempenho' type='alto' data={rankingPdvAlto} />
                  </SubChartBox>
                  <SubChartBox>
                    <RankSubChart title='Top 10 Baixo Desempenho' type='baixo' data={rankingPdvBaixo} />
                  </SubChartBox>
                </SubChartsGrid>
              </UnifiedCard>

              {!hideFamilyCharts && (
                <UnifiedCard>
                  <UnifiedCardHeader>
                    <div className='title-area'>
                      <FaBoxes />
                      <h3>Desempenho por Família (Categorias)</h3>
                    </div>
                  </UnifiedCardHeader>
                  <SubChartsGrid>
                    <SubChartBox>
                      <RankSubChart title='Top 10 Desempenho' type='alto' data={rankingFamAlto} />
                    </SubChartBox>
                    <SubChartBox>
                      <RankSubChart title='Top 10 Baixo Desempenho' type='baixo' data={rankingFamBaixo} />
                    </SubChartBox>
                  </SubChartsGrid>
                </UnifiedCard>
              )}
            </UnifiedPanelsGrid>
          </>
        )}
      </DashboardGrid>
    </PageContainer>
  )
}
